import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { compose } from 'redux';
import { reduxForm } from 'redux-form';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import Section from '../../Section';
import Timer from '../../Timer';
import { getTextColorsFromSection } from '../../lib/colors';
import Typography from '../../Typography';
import ParticipantFields from './ParticipantFields';
import BuyerFields from './BuyerFields';
import OrderSummary from '../OrderSummary';
import FormToast from '../FormToast';

function OrderForm({
  classes,
  event,
  site,
  section,
  ticketsCount,
  preOrder,
  preOrderTickets,
  timeOffset,
  handleSubmit,
  submitting,
  onPreOrderExpired,
  submitFailed,
  error,
  foreignBuyer,
  language,
  registerFormFields,
}) {
  const { styles } = section;
  const colors = getTextColorsFromSection(site, section);
  const showBuyerFields = preOrder.subtotalValue || !event.registerHideBuyerFieldsOnFreeOrders;

  return (
    <Section site={site} section={section} className={classes.root} rootClassName={classes.root}>
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.header}>
          <Typography
            type="title"
            className={classes.timer}
            style={{ ...colors.title, ...styles.title }}
          >
            <Timer to={preOrder.expireAt} offset={timeOffset} onEnd={onPreOrderExpired} />
          </Typography>
          <Typography
            type="caption"
            style={{ ...colors.title, ...styles.title }}
            className={classes.headerText}
          >
            <FormattedMessage id="preOrderExpirationMessage" />
          </Typography>
        </div>
        {!submitting &&
          submitFailed && (
            <FormToast className={classes.toast}>
              <FormattedMessage id={error ? 'orderSubmissionError' : 'orderValidationError'} />
            </FormToast>
          )}
        <div className={classes.fields}>
          {showBuyerFields && (
            <BuyerFields event={event} foreignBuyer={foreignBuyer} language={language} />
          )}
          <Typography type="sectionTitle">
            <FormattedMessage id="attendeeData" />
          </Typography>
          {preOrderTickets.map((ticket, index) => (
            <ParticipantFields
              key={index}
              index={index}
              ticket={ticket}
              fields={registerFormFields}
              foreignBuyer={foreignBuyer}
            />
          ))}
        </div>
        <OrderSummary
          event={event}
          site={site}
          section={section}
          ticketsCount={ticketsCount}
          subtotalValue={preOrder.subtotalValue}
          totalValue={preOrder.totalValue}
          submitting={submitting}
          className={classes.summary}
        />
      </form>
    </Section>
  );
}

OrderForm.propTypes = {
  classes: PropTypes.object.isRequired,
  event: PropTypes.object.isRequired,
  site: PropTypes.object.isRequired,
  section: PropTypes.object.isRequired,
  ticketsCount: PropTypes.number.isRequired,
  preOrder: PropTypes.object.isRequired,
  preOrderTickets: PropTypes.array.isRequired,
  timeOffset: PropTypes.number.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  onPreOrderExpired: PropTypes.func.isRequired,
  foreignBuyer: PropTypes.bool.isRequired,
  language: PropTypes.string.isRequired,
  registerFormFields: PropTypes.array.isRequired,
  submitFailed: PropTypes.bool,
  error: PropTypes.object,
};

OrderForm.defaultProps = {
  submitFailed: false,
  error: undefined,
};

const stylesheet = theme => ({
  root: {
    height: '100%',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: `calc(100vh - ${12 * theme.spacing.unit}px)`,
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      maxHeight: '100%',
    },
  },
  header: {
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    gridArea: 'header',
    padding: [
      [theme.spacing.small, theme.spacing.xlarge, theme.spacing.small, theme.spacing.large],
    ],
    borderBottom: [[1, 'solid', theme.palette.common.faintBlack]],
    [theme.breakpoints.down('sm')]: {
      padding: [theme.spacing.unit, theme.spacing.xlarge, theme.spacing.unit, theme.spacing.small],
    },
  },
  toast: {
    gridArea: 'error',
  },
  timer: {
    flex: '0 0 auto',
    marginRight: theme.spacing.medium,
    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing.small,
      fontSize: 14,
    },
  },
  headerText: {
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
  fields: {
    flex: '1 1 auto',
    padding: theme.spacing.large,
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing.small,
    },
  },
  button: {
    display: 'block',
    justifySelf: 'end',
    margin: 0,
  },
});

const foreignBuyerSelector = formValueSelector('order');
function mapStateToProps(state) {
  return {
    foreignBuyer: foreignBuyerSelector(state, 'buyer.foreign'),
  };
}

export default compose(
  injectSheet(stylesheet),
  reduxForm({
    form: 'order',
    destroyOnUnmount: false,
    initialValues: { paymentForm: 'creditCard', installmentsCount: '1', buyer: { foreign: false } },
  }),
  connect(mapStateToProps),
)(OrderForm);



// WEBPACK FOOTER //
// ./src/site-render/src/RegisterExtension/OrderForm/OrderForm.js